import React, { useEffect, useState } from "react";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";
import { ArrowLeft, Download, Search, RefreshCw } from "lucide-react";
import * as XLSX from "xlsx";
import { getMonthlyElectronicSubmissionReport } from "../../services/masterDataService";
import { selectStyles } from "../../helpers";
import "./MonthlyElectronicDetailsReport.css";

const pageSizeOptions = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
];

const formatDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
};

const MonthlyElectronicDetailsReport = ({
  domain,
  companyId,
  account,
  fromDate,
  toDate,
  typeOfSubmission,
  onBack,
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(pageNumber, totalPages);

  const loadPage = async (page, size, search = appliedSearch) => {
    if (!domain || !fromDate || !toDate || !companyId) {
      toast.error("Missing filters. Go back and search again.");
      return;
    }
    setLoading(true);
    try {
      const res = await getMonthlyElectronicSubmissionReport({
        domain,
        companyId,
        fromDate,
        toDate,
        typeOfSubmission: typeOfSubmission || "",
        searchText: search || "",
        tabName: "Details",
        pageNumber: page,
        pageSize: size,
        isExport: false,
      });
      const data = res?.items ? res : res?.data || {};
      setRows(data?.items || []);
      setTotalCount(data?.totalCount || 0);
      setPageNumber(page);
      setPageSize(size);
      setAppliedSearch(search || "");
      if (!(data?.items || []).length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load details");
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(1, pageSize, "");
  }, [domain, companyId, fromDate, toDate, typeOfSubmission]);

  const handleSearch = () => loadPage(1, pageSize, searchText.trim());

  const handleDownload = async () => {
    setLoading(true);
    try {
      const res = await getMonthlyElectronicSubmissionReport({
        domain,
        companyId,
        fromDate,
        toDate,
        typeOfSubmission: typeOfSubmission || "",
        searchText: appliedSearch || "",
        tabName: "Details",
        pageNumber: 1,
        pageSize: 25,
        isExport: true,
      });
      const data = res?.items ? res : res?.data || {};
      const list = data?.items || [];
      if (!list.length) {
        toast.error("No data to download");
        return;
      }
      if ((data.totalCount || 0) > list.length) {
        toast.error(`Export incomplete: ${list.length} of ${data.totalCount}`);
      }
      const mapped = list.map((r) => ({
        Account: r.account,
        CompanyId: r.companyId,
        TransactionId: r.transactionId,
        "Bill Generated Month": r.billGeneratedMonth,
        "Bill#": r.billNumber,
        "Bill Date": formatDate(r.billDate),
        "Bill Submission Date": formatDate(r.billSubmissionDate),
        "Case Type": r.caseType,
        "Type of Submission": r.typeOfSubmission,
        BillStatus: r.billStatus,
        Insurance: r.insuranceName,
        "Payer Id": r.payerId,
        "Payer Name": r.payerName,
        "Is NYSIF Carrier?": r.isNysifCarrier,
        "Claim Archived": r.claimArchived,
        Provider: r.provider,
        "Billing Address": r.billingAddress,
      }));
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Details");
      XLSX.writeFile(wb, "MonthlyElectronicSubmission_Details.xlsx");
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="med-page">
      <Toaster position="top-right" />

      <div className="med-header">
        <button type="button" className="med-btn med-btn-back" onClick={onBack}>
          <ArrowLeft size={12} />
          Back
        </button>

        <div className="med-title">
          <h3>
            {account || "Company"} ({companyId})
          </h3>
          <span>
            {fromDate} → {toDate}
            {typeOfSubmission ? ` · ${typeOfSubmission}` : " · All types"}
          </span>
        </div>

        
        <div className="med-search-row">
          <input
            className="med-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search Bill#, Transaction ID, Payer Id"
          />
          <button type="button" className="med-btn med-btn-back" onClick={handleSearch} disabled={loading}>
            <Search size={14} />
            Search
          </button>
          <button
            type="button"
            className="med-btn med-btn-reset"
            onClick={() => {
              setSearchText("");
              loadPage(1, pageSize, "");
            }}
            disabled={loading}
          >
            <RefreshCw size={14} />
            Reset
          </button>
        </div>
        
        <button
          type="button"
          className="med-btn med-btn-reset"
          onClick={handleDownload}
          disabled={loading || !rows.length}
        >
          <Download size={14} />
          Excel
        </button>
      </div> 

      <div className="med-table-wrap">
        <table className="med-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Transaction ID</th>
              <th>Bill Generated Month</th>
              <th>Bill#</th>
              <th>Bill Date</th>
              <th>Bill Submission Date</th>
              <th>Case Type</th>
              <th>Type of Submission</th>
              <th>Bill Status</th>
              <th>Insurance</th>
              <th>Payer Id</th>
              <th>Payer Name</th>
              <th>NYSIF</th>
              <th>Claim Archived</th>
              <th>Provider</th>
              <th>Billing Address</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={16} className="med-empty">
                  {loading ? "Loading..." : "No data"}
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={`${r.transactionId}|${r.billNumber}|${i}`}>
                  <td>
                    {r.account} - {r.companyId}
                  </td>
                  <td>{r.transactionId}</td>
                  <td>{r.billGeneratedMonth}</td>
                  <td>{r.billNumber}</td>
                  <td>{formatDate(r.billDate)}</td>
                  <td>{formatDate(r.billSubmissionDate)}</td>
                  <td>{r.caseType}</td>
                  <td>{r.typeOfSubmission}</td>
                  <td>{r.billStatus}</td>
                  <td>{r.insuranceName}</td>
                  <td>{r.payerId}</td>
                  <td>{r.payerName}</td>
                  <td>{r.isNysifCarrier}</td>
                  <td>{r.claimArchived}</td>
                  <td>{r.provider}</td>
                  <td>{r.billingAddress}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && (
        <div className="med-pager">
          <Select
            options={pageSizeOptions}
            value={pageSizeOptions.find((o) => o.value === pageSize)}
            onChange={(opt) => loadPage(1, opt.value, appliedSearch)}
            styles={selectStyles}
            menuPlacement="top"
            isSearchable={false}
          />
          <span>
            {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, totalCount)} of {totalCount}
          </span>
          <button
            type="button"
            className="med-btn med-btn-reset"
            disabled={safePage <= 1}
            onClick={() => loadPage(safePage - 1, pageSize, appliedSearch)}
          >
            Prev
          </button>
          <button
            type="button"
            className="med-btn med-btn-reset"
            disabled={safePage >= totalPages}
            onClick={() => loadPage(safePage + 1, pageSize, appliedSearch)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default MonthlyElectronicDetailsReport;